"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/common/AlertDialog";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface ConfirmationDialogOptions {
  title: string;
  description?: string;
  cancelText?: string;
  confirmText?: string;
  confirmButtonClassName?: string;
}

interface ConfirmationDialogContext {
  confirm: (options: ConfirmationDialogOptions) => Promise<boolean>;
}

const ConfirmationDialogContext =
  createContext<ConfirmationDialogContext | null>(null);

export const ConfirmationDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    options: ConfirmationDialogOptions | null;
    resolve: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    options: null,
    resolve: null,
  });

  const resetTimeoutRef = useRef<number | null>(null);
  const resolvedRef = useRef(false);

  const scheduleReset = useCallback(() => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }
    resetTimeoutRef.current = window.setTimeout(() => {
      setDialogState((prev) => ({
        ...prev,
        options: null,
        resolve: null,
      }));
      resolvedRef.current = false;
      resetTimeoutRef.current = null;
    }, 1000); //* Small delay to allow dialog close animation to finish
  }, []);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const confirm = useCallback((options: ConfirmationDialogOptions) => {
    return new Promise<boolean>((res) => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
      resolvedRef.current = false;
      setDialogState({
        isOpen: true,
        options,
        resolve: res,
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    setDialogState((prev) => {
      if (!resolvedRef.current) {
        prev.resolve?.(false);
        resolvedRef.current = true;
      }
      return {
        ...prev,
        isOpen: false,
      };
    });
    scheduleReset();
  }, [scheduleReset]);

  const handleConfirm = useCallback(() => {
    setDialogState((prev) => {
      if (!resolvedRef.current) {
        prev.resolve?.(true);
        resolvedRef.current = true;
      }
      return {
        ...prev,
        isOpen: false,
      };
    });
    scheduleReset();
  }, [scheduleReset]);

  return (
    <ConfirmationDialogContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog
        open={dialogState.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogState((prev) => {
              if (!resolvedRef.current) {
                prev.resolve?.(false);
                resolvedRef.current = true;
              }
              return {
                ...prev,
                isOpen: false,
              };
            });
            scheduleReset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogState.options?.title}</AlertDialogTitle>
            {dialogState.options?.description && (
              <AlertDialogDescription>
                {dialogState.options.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleClose}>
              {dialogState.options?.cancelText ?? "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={dialogState.options?.confirmButtonClassName}
            >
              {dialogState.options?.confirmText ?? "Continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationDialogContext.Provider>
  );
};

export const useConfirmationDialog = () => {
  const context = useContext(ConfirmationDialogContext);
  if (!context) {
    throw new Error(
      "useConfirmationDialog must be used within a ConfirmationDialogProvider",
    );
  }
  return context;
};
